import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  CvaFormGroupComponent,
  ResetOptions
} from '@liberty-financial/agp-shared';
import { GuidUtil } from '@liberty-financial/ng-core';

import { UserList } from '@core/services/user';
import { ContactType } from '@features/contacts/models/contactType';
import { ContactSearchFilterOptions } from '@features/contacts/services/contact.service';
import { TagService } from '@features/opportunities/services/tag.service';
import { SelectListOption } from '@shared/components/form-fields/select-list-option';
import { SortOption } from '@shared/models/sort-option';
import { Subscription } from 'rxjs';

import { sortItems } from './sort.options';

export interface ContactSearchFilterOptionsForm {
  search?: string;
  contactType?: string;
  tagFilter?: string;
  ownedBy?: string;
}

@Component({
  selector: 'spark-contact-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SparkContactFiltersComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SparkContactFiltersComponent),
      multi: true
    }
  ]
})
export class SparkContactFiltersComponent
  extends CvaFormGroupComponent<
    ContactSearchFilterOptions,
    ContactSearchFilterOptionsForm
  >
  implements OnDestroy
{
  form = this.fb.group({
    search: this.fb.control<string>(''),
    contactType: this.fb.control<string>(''),
    tagFilter: this.fb.control<string>(''),
    ownedBy: this.fb.control<string>('')
  });

  public ownedBySelectListOptions: SelectListOption[] = [];

  @Input() identifier = GuidUtil.generate();
  @Input() includeAccessible = true;
  @Input() defaultFilter: ContactSearchFilterOptions = {};
  @Input() users: UserList = new UserList([]);

  @Output() sortOptionChanged = new EventEmitter<SortOption | null>();
  selectedContactType: string[] = [];
  contactType = ContactType;

  /** sorting */
  sortItems = sortItems;
  sortOptionOpen = false;

  // User dropdown properties
  selectedUserId: string | null = null;

  tagOptions: SelectListOption[] = [];

  private tagOptionsSub?: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly tagService: TagService
  ) {
    super();
  }

  ngOnInit(): void {
    // Initialize form state - dropdown logic now handled by UserDropdownComponent
    this.selectedUserId = null;
    this.tagOptionsSub = this.tagService.getAllTags().subscribe((tags) => {
      this.tagOptions = [
        { label: 'All Tags', value: '' },
        ...tags.map((tag) => ({ label: tag.tagName, value: tag.tagId }))
      ];
    });
  }

  ngOnDestroy(): void {
    this.tagOptionsSub?.unsubscribe();
  }

  onContactTypeFilterChange(contactTypes: string[]) {
    const contactTypeCtrl = this.form.controls.contactType;
    if (contactTypeCtrl.value !== contactTypes[0]) {
      contactTypeCtrl.setValue(contactTypes[0] as ContactType);
    }
    this.selectedContactType = contactTypes;
  }

  closeSortMenu() {
    this.sortOptionOpen = false;
  }

  toggleSortMenu() {
    this.sortOptionOpen = !this.sortOptionOpen;
  }

  onSortSelected(sortOption: SortOption) {
    this.sortOptionChanged.emit(sortOption);
  }
  protected reset(opts?: ResetOptions | undefined): void {
    this.form.reset(undefined, opts);
  }

  protected transformToModel = (
    value: ContactSearchFilterOptionsForm
  ): ContactSearchFilterOptions => {
    return {
      search: value.search,
      contactType: value.contactType,
      ownedBy: value.ownedBy,
      tagId: value.tagFilter?.trim() || ''
    };
  };

  protected transformToForm = (
    value: ContactSearchFilterOptions
  ): ContactSearchFilterOptionsForm => {
    return {
      search: value.search,
      contactType: value.contactType,
      ownedBy: value.ownedBy,
      tagFilter: value.tagId ?? ''
    };
  };

  public onUserSelected(portfolioId: string): void {
    this.selectedUserId = portfolioId;
    this.form.get('ownedBy')?.setValue(portfolioId);
  }
}
